/** A plain, JSON-safe value for a Notion property we do not edit (§4.2 "extra"). */
export type ExtraValue = string | number | boolean | string[] | null;

/** Canonical contact (§4.2). Field names are the app's; Notion names live in schema.ts. */
export interface Contact {
  id: string;
  companyName: string;
  contact: string;
  contactRole: string | null;
  email: string | null;
  phone: string | null;
  linkedin: string | null;
  companySite: string | null;
  stage: string | null;
  status: string | null;
  /** First value of Notion's multi-select (D-12). */
  nextAction: string | null;
  /** Remaining values, shown as "+n", preserved until the app sets a new value. */
  nextActionExtra: string[];
  /** Notion user ids. */
  owner: string[];
  /** ISO date (YYYY-MM-DD) or null. */
  nextContactDate: string | null;
  lastContactDate: string | null;
  leadSource: string | null;
  industry: string | null;
  description: string;
  createdTime: string;
  lastEditedTime: string;
  lastEditedBy: string | null;
  url: string;
  archived: boolean;
  /** Every other Notion property, keyed by its (trimmed) Notion name. */
  extra: Record<string, ExtraValue>;
}

/** Fields the app may write (§4.2 canonical mapping minus read-only ones). */
export type WritableContactField =
  | 'companyName'
  | 'contact'
  | 'contactRole'
  | 'email'
  | 'phone'
  | 'linkedin'
  | 'companySite'
  | 'stage'
  | 'status'
  | 'nextAction'
  | 'owner'
  | 'nextContactDate'
  | 'lastContactDate'
  | 'leadSource'
  | 'industry';

export type ContactPatch = Partial<Pick<Contact, WritableContactField>>;

export type LogType =
  | 'note'
  | 'create'
  | 'update'
  | 'follow-up'
  | 'snooze'
  | 'archive'
  | 'restore'
  | 'suggestion'
  | 'external'
  | 'revert'
  | 'message';

export type LogSource = 'app' | 'notion' | 'system' | 'telegram';

/** Where a sent message came from (§9): the AI draft as-is, the draft edited by the user, or typed by hand. */
export type MessageOrigin = 'ai' | 'ai-edited' | 'manual';

export interface Change {
  /** Notion property name. */
  p: string;
  from: ExtraValue;
  to: ExtraValue;
}

/** Canonical CRM Log entry (§4.3). */
export interface LogEntry {
  id: string;
  name: string;
  contactId: string | null;
  type: LogType;
  actor: string | null;
  actorEmail: string | null;
  at: string;
  text: string;
  changes: Change[];
  actionId: string | null;
  reverts: string | null;
  reverted: boolean;
  source: LogSource;
  conflict: boolean;
  /** `message` entries only. */
  origin: MessageOrigin | null;
  archived: boolean;
  lastEditedTime: string;
  /** Client only: provisional row written by the outbox, replaced by the server's row on pull (§5.4). */
  pending?: boolean;
}

/** `Change.p` for the page's archived flag (archive/restore primitives). */
export const ARCHIVED_P = '$archived';
/** `Change.p` prefix for a CRM Log entry's archived flag (archiveLog/restoreLog primitives). */
export const LOG_ARCHIVED_PREFIX = '$log:';

export interface SelectOption {
  name: string;
  color: string;
}

/** Option lists per Notion property name, synced from the data source schema (§4.2). */
export type SchemaOptions = Record<string, SelectOption[]>;

/** A Notion workspace person, for owner names and the admin mapping (§3). */
export interface Person {
  id: string;
  name: string;
  email: string | null;
  avatarUrl: string | null;
}

export type Role = 'member' | 'admin';

/** Digest settings as the user sees them (§8). The CallMeBot key is write-only: only `hasKey` comes back. */
export type DigestChannel = 'telegram' | 'callmebot';

export interface DigestSettings {
  enabled: boolean;
  /** 0–23 in the user's time zone. */
  hour: number;
  /** §19 D-33: Telegram once linked, else CallMeBot. */
  channel: DigestChannel;
  phone: string | null;
  hasKey: boolean;
  lastSentDate: string | null;
  lastError: string | null;
  failures: number;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  notionUserId: string | null;
  timeZone: string;
  locale: 'en' | 'bg';
  digest: DigestSettings;
  /** §19 D-31: the linked Telegram account, if any. */
  telegram: { linked: boolean; name: string | null };
}

/** `POST /api/me/telegram/link` (§19.2): a one-time code and the deep link that carries it. */
export interface TelegramLinkCode {
  code: string;
  url: string;
  expiresAt: string;
}

/** `PATCH /api/me` (§6.5). */
export interface ProfileUpdate {
  name?: string;
  locale?: 'en' | 'bg';
  timeZone?: string;
  digest?: { enabled?: boolean; hour?: number; channel?: DigestChannel; phone?: string | null; callMeBotKey?: string | null };
}

/** Admin's view of a user (§6.6): no digest secrets. */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  notionUserId: string | null;
  timeZone: string;
  digestEnabled: boolean;
  digestChannel: DigestChannel;
  telegramLinked: boolean;
  digestFailures: number;
  digestLastError: string | null;
  createdAt: string;
}

/** The digest as a model (§8, D-13), rendered per channel. */
export interface DigestItem {
  contactId: string;
  company: string;
  contact: string;
  nextAction: string | null;
  date: string;
  /** Days overdue (positive) or 0 for today. */
  overdue: number;
}
export interface DigestModel {
  userName: string;
  date: string;
  groups: { title: string; items: DigestItem[] }[];
  link: string;
}
export interface DigestPreview {
  model: DigestModel;
  text: string;
  /** Characters in `text` (CallMeBot sends via GET, ~900 max). */
  length: number;
  canSend: boolean;
}
export interface DigestSendResult {
  sent: boolean;
  reason?: 'empty' | 'disabled' | 'no-key' | 'no-telegram' | 'error';
  error?: string;
  length?: number;
  channel?: DigestChannel;
}

/** `POST /api/ai/extract` (§9). */
export interface ExtractRequest {
  company: string;
  contact: string;
  notes: string;
  /** Known prospect: its last notes are added to the prompt. */
  contactId?: string;
}
export interface ExtractProposal {
  value: string | null;
  /** 0–1. */
  confidence: number;
  /** The value is not in Notion's option list; the user must confirm (D-14). */
  isNew: boolean;
}
export interface ExtractResponse {
  /** Person and company the notes name (§19 captures have no form fields). */
  name: ExtractProposal;
  company: ExtractProposal;
  contactRole: ExtractProposal;
  industry: ExtractProposal;
  nextAction: ExtractProposal;
  suggestedMessage: { value: string | null; confidence: number };
  model: string;
}

export type Synced<T> = T & { rev: number };

/** `GET /api/sync?since=N` (§5.4). */
export interface SyncResponse {
  rev: number;
  /** true when `since` was 0 or too old and the client must replace its replica. */
  full: boolean;
  contacts: Synced<Contact>[];
  log: Synced<LogEntry>[];
  drafts?: Synced<ContactDrafts>[];
  schema?: SchemaOptions;
  people?: Person[];
}

/** `POST /api/actions` response (§5.4). */
export interface ActionResult {
  ok: true;
  rev: number;
  /** Client temp id → Notion page id for created contacts. */
  mapping: Record<string, string>;
  /** Id of the CRM Log entry this action wrote. */
  entryId: string;
  conflict: boolean;
}

/** `POST /api/ai/message` (§9). */
export interface MessageDraftsRequest {
  contactId: string;
  /** Steering from the chips / hint box, e.g. "shorter, in Bulgarian". */
  hint?: string;
  /** Drafts already shown; the next batch must differ from them. */
  exclude?: string[];
}

export interface MessageDraftsResponse {
  drafts: string[];
  model: string;
}

/** Pre-generated drafts for a contact (§9, D-28): a rebuildable cache keyed by the context they were made from. */
export interface ContactDrafts {
  contactId: string;
  /** Hash of the prompt context (contact fields + recent history); drafts are stale when it changes. */
  hash: string;
  drafts: string[];
  model: string;
  createdAt: string;
}

/** Ask the CRM (§20, D-39): one question and its answer; `contacts` are the ones the answer cited. */
export interface AskAnswer {
  id: string;
  question: string;
  answer: string;
  contacts: { id: string; name: string }[];
  model: string;
  at: string;
}

export interface SyncHealth {
  /** D-28 background drafting: calls made today and the daily budget. */
  draftsToday?: number;
  draftsBudget?: number;
  lastPollAt: string | null;
  lastWriteAt: string | null;
  logDataSourceId: string | null;
  lastPollOk: boolean;
  lastError: string | null;
  errorCount: number;
  cursor: string | null;
  rev: number;
  contacts: number;
  live: boolean;
}
